import { API_BASE_URL } from "../../config";
import { Collection } from "./types/collection";
import { getAuthHeaders } from "../Auth/authService";
import { collectionStore } from "./collectionStore";
import { Mod, ModState, ModScope } from "../Mod/types/mod";

let listAbortController: AbortController | null = null;

const abortList = (): void =>
{
	if (listAbortController)
	{
		listAbortController.abort();
		listAbortController = null;
	}
};

const mapMod = (mod: Mod): Mod => ({
	id: mod.id,
	state: mod.state as ModState,
	reportedSize: mod.reportedSize,
	downloadProgress: mod.downloadProgress,
	sizeOnDisk: mod.sizeOnDisk,
	image: mod.image,
	title: mod.title,
	updateDate: mod.updateDate ? new Date(mod.updateDate) : undefined,
	workshopUpdateDate: new Date(mod.workshopUpdateDate),
	postedDate: new Date(mod.postedDate),
	tags: mod.tags,
	scope: mod.scope as ModScope,
	isSigned: mod.isSigned,
});

const mapCollection = (collection: Collection): Collection => ({
	id: collection.id,
	name: collection.name,
	mods: collection.mods.map(mapMod),
});

const list = async (): Promise<Collection[]> =>
{
	abortList();
	listAbortController = new AbortController();

	const response = await fetch(`${API_BASE_URL}/collections`, {
		method: "GET",
		headers: getAuthHeaders(),
		signal: listAbortController.signal
	});

	if (!response.ok)
	{
		throw new Error(response.statusText);
	}

	const result = await response.json();
	const collections = result.map(mapCollection);

	listAbortController = null;
	collectionStore.set(collections);
	return collections;
};

const get = async (id: string): Promise<Collection | null> =>
{
	const response = await fetch(`${API_BASE_URL}/collections/${id}`, {
		method: "GET",
		headers: getAuthHeaders()
	});

	if (response.status === 404)
	{
		return null;
	}

	if (!response.ok)
	{
		throw new Error(response.statusText);
	}

	const result = await response.json();
	const collection = mapCollection(result);

	collectionStore.update(collection);
	return collection;
};

const create = async (name: string, modIds: string[] = []): Promise<Collection> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/collections`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ name, modIds })
	});

	if (!response.ok)
	{
		throw new Error("Failed to create collection");
	}

	const result = await response.json();
	const collection = mapCollection(result);

	collectionStore.add(collection);
	return collection;
};

const update = async (collection: Partial<Collection> & { id: string; }): Promise<Collection | null> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/collections/${collection.id}`, {
		method: "PUT",
		headers: getAuthHeaders(),
		body: JSON.stringify({ name: collection.name })
	});

	if (response.status === 404)
	{
		return null;
	}

	if (!response.ok)
	{
		throw new Error("Failed to update collection");
	}

	const result = await response.json();
	const updatedCollection = mapCollection(result);

	collectionStore.update(updatedCollection);
	return updatedCollection;
};

const remove = async (id: string): Promise<void> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/collections/${id}`, {
		method: "DELETE",
		headers: getAuthHeaders()
	});

	if (!response.ok)
	{
		throw new Error("Failed to delete collection");
	}

	collectionStore.remove(id);
};

const addMod = async (collectionId: string, modId: string): Promise<Collection | null> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/mods`, {
		method: "POST",
		headers: getAuthHeaders(),
		body: JSON.stringify({ modId })
	});

	if (response.status === 404)
	{
		return null;
	}

	if (!response.ok)
	{
		throw new Error("Failed to add mod to collection");
	}

	const result = await response.json();
	const collection = mapCollection(result);

	collectionStore.update(collection);
	return collection;
};

const removeMod = async (collectionId: string, modId: string): Promise<Collection | null> =>
{
	abortList();

	const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/mods/${modId}`, {
		method: "DELETE",
		headers: getAuthHeaders()
	});

	if (response.status === 404)
	{
		return null;
	}

	if (!response.ok)
	{
		throw new Error("Failed to remove mod from collection");
	}

	const result = await response.json();
	const collection = mapCollection(result);

	collectionStore.update(collection);
	return collection;
};

const extractWorkshopId = (url: string): string | null =>
{
	try
	{
		const parsedUrl = new URL(url);
		const id = parsedUrl.searchParams.get("id")?.trim();

		if (!id || !/^\d+$/.test(id))
		{
			return null;
		}

		return id;
	}
	catch
	{
		return null;
	}
};

const parseArma3LauncherPreset = (html: string, fallbackName: string): { name: string; modIds: string[]; } =>
{
	const parser = new DOMParser();
	const document = parser.parseFromString(html, "text/html");

	const parserError = document.querySelector("parsererror");
	if (parserError)
	{
		throw new Error("Invalid Arma 3 preset file");
	}

	const presetName =
		document.querySelector('meta[name="arma:PresetName"]')?.getAttribute("content")?.trim()
		?? fallbackName;

	const links = Array.from(document.querySelectorAll('a[data-type="Link"]'));
	const modIds = [...new Set(links
		.map(link => link.getAttribute("href") ?? "")
		.map(extractWorkshopId)
		.filter((id): id is string => Boolean(id)))];

	if (modIds.length === 0)
	{
		throw new Error("No Steam Workshop mod links found in preset");
	}

	return {
		name: presetName,
		modIds,
	};
};

export const importArma3LauncherPreset = async (file: File): Promise<void> =>
{
	const html = await file.text();
	const fallbackName = file.name.replace(/\.html$/i, "");
	const { name, modIds } = parseArma3LauncherPreset(html, fallbackName);

	await create(name, modIds);
};

const escapeHtml = (value: string): string =>
	value.replace(/[&<>"']/g, (char) =>
		({
			"&": "&amp;",
			"<": "&lt;",
			">": "&gt;",
			"\"": "&quot;",
			"'": "&#39;",
		}[char] ?? char));

export const exportArma3LauncherPreset = (collection: Collection): void =>
{
	const modRows = collection.mods.map(mod =>
	{
		const url = `https://steamcommunity.com/sharedfiles/filedetails/?id=${mod.id}`;
		return `		<tr data-type="ModContainer">
			<td data-type="DisplayName">${escapeHtml(mod.title)}</td>
			<td><span class="from-steam">Steam</span></td>
			<td><a href="${url}" data-type="Link">${url}</a></td>
		</tr>`;
	}).join("\n");

	const html = `<?xml version="1.0" encoding="utf-8"?>
<html>
  <!--Created by Arma 3 Launcher: https://arma3.com-->
  <head>
    <meta name="arma:Type" content="preset" />
    <meta name="arma:PresetName" content="${escapeHtml(collection.name)}" />
    <meta name="generator" content="Arma 3 Launcher - https://arma3.com" />
    <title>Arma 3</title>
  </head>
  <body>
    <h1>Arma 3  - Preset <strong>${escapeHtml(collection.name)}</strong></h1>
    <div class="mod-list">
      <table>
${modRows}
      </table>
    </div>
    <div class="dlc-list"><table /></div>
    <div class="footer"><span>Created by Arma 3 Launcher by Bohemia Interactive.</span></div>
  </body>
</html>`;

	const blob = new Blob([html], { type: "text/html" });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = `${collection.name}.html`;
	anchor.click();
	URL.revokeObjectURL(url);
};

export const collectionService = {
	list,
	get,
	create,
	update,
	remove,
	addMod,
	removeMod,
};
